import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Plus, 
  Download, 
  Trash2, 
  Search,
  FileText,
  Eye,
  CheckCircle,
  Clock,
  Send,
  Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const getStatusColor = (status) => {
  const colors = {
    draft: "bg-slate-100 text-slate-800",
    sent: "bg-blue-100 text-blue-800",
    paid: "bg-green-100 text-green-800"
  };
  return colors[status] || "bg-slate-100 text-slate-800";
};

const getStatusIcon = (status) => {
  const icons = {
    draft: Clock,
    sent: Send,
    paid: CheckCircle
  };
  return icons[status] || Clock;
};

const AdminInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    invoice_number: "",
    client_id: "",
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: "",
    service_period: "",
    payment_terms: "Net 30",
    items: [{ description: "", quantity: 1, rate: 0 }],
    tax_rate: 0,
    notes: "",
    filename: ""
  });

  useEffect(() => {
    fetchInvoices();
    fetchClients();
  }, [statusFilter]);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      let url = `${API}/invoices`;
      if (statusFilter !== "all") url += `?status=${statusFilter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const getNextInvoiceNumber = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/invoices/next-number`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.next_number;
    } catch (error) {
      return "0001";
    }
  };

  const handleCreate = async () => {
    const nextNumber = await getNextInvoiceNumber();
    const today = new Date();
    const dueDate = new Date(today);
    dueDate.setDate(dueDate.getDate() + 30);
    
    setFormData({
      invoice_number: nextNumber,
      client_id: "",
      invoice_date: today.toISOString().split('T')[0],
      due_date: dueDate.toISOString().split('T')[0],
      service_period: `${today.toLocaleString('default', { month: 'long' })} ${today.getFullYear()}`,
      payment_terms: "Net 30",
      items: [{ description: "", quantity: 1, rate: 0 }],
      tax_rate: 0,
      notes: "",
      filename: ""
    });
    setIsModalOpen(true);
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setIsViewOpen(true);
  };

  const handleDelete = (invoice) => {
    setSelectedInvoice(invoice);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/invoices/${selectedInvoice.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invoice deleted successfully");
      fetchInvoices();
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Failed to delete invoice");
    } finally {
      setIsDeleteOpen(false);
      setSelectedInvoice(null);
    }
  };

  const updateStatus = async (invoiceId, newStatus) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/invoices/${invoiceId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invoice marked as ${newStatus}`);
      fetchInvoices();
      if (selectedInvoice) {
        setSelectedInvoice({ ...selectedInvoice, status: newStatus });
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: 1, rate: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== index)
      });
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (formData.tax_rate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const generatePDF = (invoice, download = true) => {
    const client = clients.find(c => c.id === invoice.client_id);
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    
    // Font sizes - consistent throughout
    const TITLE_SIZE = 24;
    const HEADER_SIZE = 11;
    const BODY_SIZE = 10;
    const TABLE_SIZE = 11;
    const FOOTER_TITLE_SIZE = 12;
    const FOOTER_TEXT_SIZE = 10;
    
    // Use Times font (closest to Georgia available in jsPDF)
    doc.setFont("times", "normal");
    
    // ===== LETTERHEAD HEADER =====
    // Top accent bar
    doc.setFillColor(30, 58, 138); // Navy blue
    doc.rect(0, 0, pageWidth, 8, 'F');
    
    // Add logo
    const logoUrl = "https://customer-assets.emergentagent.com/job_omni-solutions-1/artifacts/nc5fgtrv_Logo.jpg";
    try {
      doc.addImage(logoUrl, 'JPEG', margin, 12, 30, 12);
    } catch (e) {
      // If logo fails, show company name
      doc.setFontSize(15);
      doc.setFont("times", "bold");
      doc.setTextColor(30, 58, 138);
      doc.text("OMNILINK SOLUTIONS", margin, 20);
    }
    
    // INVOICE title on right
    doc.setFontSize(TITLE_SIZE);
    doc.setTextColor(30, 58, 138);
    doc.setFont("times", "bold");
    doc.text("INVOICE", pageWidth - margin, 22, { align: "right" });
    
    // Header underline
    doc.setDrawColor(30, 58, 138);
    doc.setLineWidth(0.5);
    doc.line(margin, 28, pageWidth - margin, 28);
    
    // ===== LEFT SIDE: Invoice No & Date =====
    let yPos = 38;
    doc.setFontSize(HEADER_SIZE);
    doc.setFont("times", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(`Invoice No: ${invoice.invoice_number}`, margin, yPos);
    yPos += 6;
    doc.setFont("times", "normal");
    doc.setFontSize(BODY_SIZE);
    doc.text(`Date: ${formatDateForPDF(invoice.invoice_date)}`, margin, yPos);
    
    // ===== RIGHT SIDE: Payment Terms, Due Date, Service Period =====
    let rightY = 38;
    const rightX = 120;
    doc.setFontSize(BODY_SIZE);
    doc.setFont("times", "normal");
    doc.text(`Payment Terms: ${invoice.payment_terms}`, rightX, rightY);
    rightY += 6;
    doc.text(`Due Date: ${formatDateForPDF(invoice.due_date)}`, rightX, rightY);
    rightY += 6;
    doc.text(`Service Period: ${invoice.service_period || 'N/A'}`, rightX, rightY);
    
    // ===== FROM SECTION (LEFT SIDE) =====
    yPos = 58;
    doc.setFontSize(HEADER_SIZE);
    doc.setFont("times", "bold");
    doc.setTextColor(30, 58, 138);
    doc.text("From:", margin, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 6;
    doc.setFontSize(BODY_SIZE);
    doc.setFont("times", "normal");
    doc.text("Omnilink Solutions", margin, yPos);
    yPos += 5;
    doc.text("211, Ganesh Glory 11,", margin, yPos);
    yPos += 5;
    doc.text("Ahmedabad, GJ, INDIA - 382470", margin, yPos);
    yPos += 5;
    doc.text("GST: 24BEXPA1662C1ZT", margin, yPos);
    yPos += 5;
    doc.setTextColor(0, 0, 200);
    doc.text("Email: info@omnilinksolutions.net", margin, yPos);
    doc.setTextColor(0, 0, 0);
    
    // ===== BILL TO SECTION (RIGHT SIDE) =====
    const billToX = 115;
    let billToY = 58;
    doc.setFontSize(HEADER_SIZE);
    doc.setFont("times", "bold");
    doc.setTextColor(30, 58, 138);
    doc.text("Bill To:", billToX, billToY);
    doc.setTextColor(0, 0, 0);
    billToY += 6;
    doc.setFontSize(BODY_SIZE);
    doc.setFont("times", "normal");
    doc.text(invoice.client_company || client?.company || "", billToX, billToY);
    
    if (client?.address) {
      const addressLines = doc.splitTextToSize(client.address, 75);
      addressLines.forEach(line => {
        billToY += 5;
        doc.text(line, billToX, billToY);
      });
    }
    if (client?.country) {
      billToY += 5;
      doc.text(client.country, billToX, billToY);
    }
    if (client?.name) {
      billToY += 5;
      doc.text(`Contact: ${client.name}`, billToX, billToY);
    }
    if (client?.email) {
      billToY += 5;
      doc.setTextColor(0, 0, 200);
      doc.text(`Email: ${client.email}`, billToX, billToY);
      doc.setTextColor(0, 0, 0);
    }
    
    // Calculate the next Y position based on whichever section is taller
    yPos = Math.max(yPos, billToY) + 10;
    
    const tableData = invoice.items.map(item => [
      item.description,
      item.quantity.toString(),
      `$${item.rate.toFixed(2)}`,
      `$${item.amount.toFixed(2)}`
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [["Service Description", "Quantity", "Rate", "Amount"]],
      body: tableData,
      theme: "grid",
      headStyles: { 
        fillColor: [30, 58, 138],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: TABLE_SIZE,
        font: "times",
        cellPadding: 4
      },
      bodyStyles: {
        fontSize: TABLE_SIZE,
        font: "times",
        textColor: [0, 0, 0],
        cellPadding: 4
      },
      columnStyles: {
        0: { cellWidth: 85 },
        1: { cellWidth: 25, halign: "center" },
        2: { cellWidth: 30, halign: "center" },
        3: { cellWidth: 30, halign: "center" }
      },
      tableWidth: 170,
      margin: { left: margin },
      tableLineColor: [100, 100, 100],
      tableLineWidth: 0.3
    });
    
    let finalY = doc.lastAutoTable.finalY + 5;
    
    // ===== TOTALS - Right aligned =====
    doc.setFontSize(BODY_SIZE);
    doc.setFont("times", "normal");
    doc.text(`Sub Total: $${invoice.subtotal.toFixed(2)}`, pageWidth - margin, finalY, { align: "right" });
    
    if (invoice.tax_rate > 0) {
      finalY += 6;
      doc.text(`Tax (${invoice.tax_rate}%): $${invoice.tax_amount.toFixed(2)}`, pageWidth - margin, finalY, { align: "right" });
    }
    
    // Total Amount with green highlight
    finalY += 8;
    doc.setFont("times", "bold");
    doc.setFillColor(144, 238, 144); // Light green
    const totalText = `Total Amount: $${invoice.total.toFixed(2)}`;
    const totalWidth = doc.getTextWidth(totalText) + 6;
    doc.rect(pageWidth - margin - totalWidth, finalY - 5, totalWidth + 2, 8, 'F');
    doc.setTextColor(0, 0, 0);
    doc.text(totalText, pageWidth - margin, finalY, { align: "right" });
    
    // Store the Y position after totals for Contact Info on right
    const contactStartY = finalY + 8;
    
    // ===== PAYMENT INSTRUCTIONS (LEFT SIDE) =====
    finalY += 12;
    
    doc.setFont("times", "bold");
    doc.setFontSize(HEADER_SIZE);
    doc.setTextColor(30, 58, 138);
    doc.text("Payment Instructions:", margin, finalY);
    
    finalY += 6;
    doc.setFont("times", "bold");
    doc.setFontSize(BODY_SIZE);
    doc.setTextColor(0, 0, 0);
    doc.text("Preferred Method: ACH Transfer", margin, finalY);
    
    finalY += 5;
    doc.setFont("times", "normal");
    doc.text("Bank Name: Citibank", margin, finalY);
    finalY += 5;
    doc.text("Bank Address: 111 Wall Street, New York, NY 10043, USA", margin, finalY);
    finalY += 5;
    doc.text("Routing (ABA): 031100209", margin, finalY);
    finalY += 5;
    doc.text("SWIFT Code: CITIUS33", margin, finalY);
    finalY += 5;
    doc.text("Account Number: 7058281001989172", margin, finalY);
    finalY += 5;
    doc.text("Account Type: CHECKING", margin, finalY);
    finalY += 5;
    doc.text("Beneficiary Name: Nikhil Ahuja", margin, finalY);
    
    // Alternative Payment Methods
    finalY += 7;
    doc.setFont("times", "bold");
    doc.text("Alternative Payment Methods:", margin, finalY);
    finalY += 5;
    doc.setFont("times", "normal");
    doc.text("Payoneer: tonikhilahuja1998@gmail.com", margin, finalY);
    finalY += 5;
    doc.text("PayPal: omnisolutions9696@gmail.com", margin, finalY);
    
    // ===== CONTACT INFORMATION (RIGHT SIDE - below Total Amount) =====
    const rightColX = 115;
    let contactY = contactStartY;
    
    doc.setFont("times", "bold");
    doc.setFontSize(HEADER_SIZE);
    doc.setTextColor(30, 58, 138);
    doc.text("Contact Information:", rightColX, contactY);
    
    contactY += 6;
    doc.setFont("times", "normal");
    doc.setFontSize(BODY_SIZE);
    doc.setTextColor(0, 0, 0);
    doc.text("Questions about this invoice:", rightColX, contactY);
    contactY += 5;
    doc.setTextColor(0, 0, 200);
    doc.text("Email: info@omnilinksolutions.net", rightColX, contactY);
    doc.setTextColor(0, 0, 0);
    contactY += 5;
    doc.text("Phone: +91-9587064194", rightColX, contactY);
    contactY += 5;
    doc.text("Contact: Nikhil Ahuja, Director", rightColX, contactY);
    
    // ===== LETTERHEAD FOOTER =====
    const footerY = pageHeight - 25;
    
    // Footer line
    doc.setDrawColor(30, 58, 138);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY, pageWidth - margin, footerY);
    
    // Company name in Navy Blue Bold
    doc.setFontSize(FOOTER_TITLE_SIZE);
    doc.setFont("times", "bold");
    doc.setTextColor(30, 58, 138);
    doc.text("Omnilink Solutions", pageWidth / 2, footerY + 6, { align: "center" });
    
    // Footer address and contact
    doc.setFontSize(FOOTER_TEXT_SIZE);
    doc.setFont("times", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text("211, Ganesh Glory 11, Gota, Ahmedabad, GJ, India - 382470", pageWidth / 2, footerY + 12, { align: "center" });
    doc.text("Email: info@omnilinksolutions.net | Phone: +91-9587064194 | www.omnilinksolutions.net", pageWidth / 2, footerY + 17, { align: "center" });
    
    // Bottom accent bar
    doc.setFillColor(30, 58, 138);
    doc.rect(0, pageHeight - 6, pageWidth, 6, 'F');
    
    if (download) {
      doc.save(`${invoice.filename || `Invoice_${invoice.invoice_number}`}.pdf`);
      toast.success("Invoice downloaded successfully");
    }
    
    return doc;
  };
  
  const formatDateForPDF = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "2-digit"
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id || formData.items.some(item => !item.description)) {
      toast.error("Please select a client and fill in all service descriptions");
      return;
    }

    const selectedClient = clients.find(c => c.id === formData.client_id);
    const filename = formData.filename || `${formData.invoice_number}_${selectedClient?.company?.replace(/[^a-zA-Z0-9]/g, '')}_${formData.service_period.replace(/\s/g, '')}`;

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/invoices`, {
        ...formData,
        filename
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Invoice created successfully");
      
      // Generate and download PDF
      const invoice = response.data.invoice;
      generatePDF(invoice);
      
      setIsModalOpen(false);
      fetchInvoices();
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast.error("Failed to create invoice");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const filteredInvoices = invoices.filter(invoice =>
    invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.client_company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.filename?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Invoices</h1>
          <p className="text-slate-600 mt-1">Generate and manage client invoices</p>
        </div>
        <Button 
          onClick={handleCreate}
          data-testid="create-invoice-btn"
          className="bg-[#1e3a8a] hover:bg-[#172554]"
          disabled={clients.length === 0}
        >
          <Plus className="w-4 h-4 mr-2" /> Create Invoice
        </Button>
      </div>

      {clients.length === 0 && (
        <Card className="border-0 shadow-sm mb-6 bg-yellow-50 border-l-4 border-l-yellow-500">
          <CardContent className="p-4">
            <p className="text-yellow-800">
              You need to add clients first before creating invoices. 
              <a href="/admin/clients" className="underline ml-1 font-medium">Add a client now</a>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-invoices-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Invoices</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoices List */}
      <div className="space-y-4">
        {filteredInvoices.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No invoices found</p>
              {clients.length > 0 && (
                <Button onClick={handleCreate} variant="outline" className="mt-4">
                  Create your first invoice
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredInvoices.map((invoice, index) => {
            const StatusIcon = getStatusIcon(invoice.status);
            return (
              <motion.div
                key={invoice.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#1e3a8a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FileText className="w-6 h-6 text-[#1e3a8a]" />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-lg font-semibold text-slate-900">#{invoice.invoice_number}</h3>
                            <Badge className={getStatusColor(invoice.status)}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {invoice.status}
                            </Badge>
                          </div>
                          <p className="text-slate-600">{invoice.client_company}</p>
                          <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(invoice.invoice_date)}
                            </span>
                            <span className="font-semibold text-[#1e3a8a]">
                              ${invoice.total.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewInvoice(invoice)}
                          data-testid={`view-invoice-${invoice.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => generatePDF(invoice)}
                          data-testid={`download-invoice-${invoice.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDelete(invoice)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-invoice-${invoice.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Create Invoice Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Invoice</DialogTitle>
            <DialogDescription>
              Generate a professional invoice for your client
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 py-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="invoice_number">Invoice Number *</Label>
                <Input
                  id="invoice_number"
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  placeholder="0001"
                  required
                  data-testid="invoice-number-input"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="client_id">Select Client *</Label>
                <Select 
                  value={formData.client_id} 
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                >
                  <SelectTrigger data-testid="client-select">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company} - {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="invoice_date">Invoice Date</Label>
                <Input
                  id="invoice_date"
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                  data-testid="invoice-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  data-testid="due-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service_period">Service Period</Label>
                <Input
                  id="service_period"
                  value={formData.service_period}
                  onChange={(e) => setFormData({ ...formData, service_period: e.target.value })}
                  placeholder="e.g. January 2026"
                  data-testid="service-period-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_terms">Payment Terms</Label>
              <Select 
                value={formData.payment_terms} 
                onValueChange={(value) => setFormData({ ...formData, payment_terms: value })}
              >
                <SelectTrigger data-testid="payment-terms-select">
                  <SelectValue placeholder="Select payment terms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                  <SelectItem value="Net 15">Net 15</SelectItem>
                  <SelectItem value="Net 30">Net 30</SelectItem>
                  <SelectItem value="Net 45">Net 45</SelectItem>
                  <SelectItem value="Net 60">Net 60</SelectItem>
                  <SelectItem value="Monthly Net 30">Monthly Net 30</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Services/Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Services / Items</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addItem}
                  data-testid="add-item-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>
              
              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-slate-50 rounded-lg">
                    <div className="col-span-6 space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => updateItem(index, "description", e.target.value)}
                        placeholder="Service description"
                        data-testid={`item-desc-${index}`}
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, "quantity", parseFloat(e.target.value) || 0)}
                        data-testid={`item-qty-${index}`}
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Rate ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.rate}
                        onChange={(e) => updateItem(index, "rate", parseFloat(e.target.value) || 0)}
                        data-testid={`item-rate-${index}`}
                      />
                    </div>
                    <div className="col-span-1 text-right font-medium text-sm">
                      ${(item.quantity * item.rate).toFixed(2)}
                    </div>
                    <div className="col-span-1">
                      {formData.items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-8"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-medium">${calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-600">Tax (%):</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                    className="w-20 h-8"
                    data-testid="tax-rate-input"
                  />
                  <span className="text-sm font-medium">${calculateTax().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total:</span>
                  <span className="text-[#1e3a8a]">${calculateTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes for the invoice..."
                rows={2}
                data-testid="invoice-notes-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="filename">PDF Filename</Label>
              <Input
                id="filename"
                value={formData.filename}
                onChange={(e) => setFormData({ ...formData, filename: e.target.value })}
                placeholder="Leave blank for auto-generated filename"
                data-testid="invoice-filename-input"
              />
              <p className="text-xs text-slate-500">
                Auto-generated: {formData.invoice_number}_{clients.find(c => c.id === formData.client_id)?.company?.replace(/[^a-zA-Z0-9]/g, '') || 'Client'}_{formData.service_period.replace(/\s/g, '')}
              </p>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-[#1e3a8a] hover:bg-[#172554]"
                data-testid="create-invoice-submit"
              >
                {isSubmitting ? "Creating..." : "Create & Download PDF"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Invoice Modal */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invoice #{selectedInvoice?.invoice_number}</DialogTitle>
            <DialogDescription>
              Invoice for {selectedInvoice?.client_company}
            </DialogDescription>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-6 py-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-medium">{selectedInvoice.client_company}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={getStatusColor(selectedInvoice.status)}>
                    {selectedInvoice.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Invoice Date</p>
                  <p className="font-medium">{formatDate(selectedInvoice.invoice_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Due Date</p>
                  <p className="font-medium">{formatDate(selectedInvoice.due_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Service Period</p>
                  <p className="font-medium">{selectedInvoice.service_period}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Payment Terms</p>
                  <p className="font-medium">{selectedInvoice.payment_terms}</p>
                </div>
              </div>

              {/* Items */}
              <div>
                <p className="text-sm text-slate-500 mb-2">Services</p>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  {selectedInvoice.items.map((item, index) => (
                    <div key={index} className="flex justify-between">
                      <span>{item.description} (x{item.quantity})</span>
                      <span className="font-medium">${item.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span>${selectedInvoice.subtotal.toFixed(2)}</span>
                    </div>
                    {selectedInvoice.tax_rate > 0 && (
                      <div className="flex justify-between text-sm text-slate-600">
                        <span>Tax ({selectedInvoice.tax_rate}%)</span>
                        <span>${selectedInvoice.tax_amount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold text-[#1e3a8a]">
                      <span>Total</span>
                      <span>${selectedInvoice.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Actions */}
              <div>
                <Label className="text-slate-700 font-medium mb-3 block">Update Status</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedInvoice.id, "sent")}
                    className={selectedInvoice.status === "sent" ? "border-blue-500 bg-blue-50" : ""}
                  >
                    <Send className="w-4 h-4 mr-1" /> Mark as Sent
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => updateStatus(selectedInvoice.id, "paid")}
                    className={selectedInvoice.status === "paid" ? "bg-green-600" : "bg-[#1e3a8a] hover:bg-[#172554]"}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Mark as Paid
                  </Button>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => generatePDF(selectedInvoice)}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsViewOpen(false)}
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete invoice #{selectedInvoice?.invoice_number}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-invoice"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminInvoices;
